import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, GitBranch, Trash2 } from 'lucide-react';

export default function Funnels() {
  const [funnels, setFunnels] = useState([]);
  const [products, setProducts] = useState([]);
  const [pages, setPages] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', product_id: '', page_id: '', flow_type: 'page_checkout_delivery' });

  useEffect(() => {
    api.get('/funnels').then(r => setFunnels(r.data)).catch(() => {});
    api.get('/products').then(r => setProducts(r.data)).catch(() => {});
    api.get('/pages').then(r => setPages(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    try {
      const { data } = await api.post('/funnels', form);
      setFunnels(prev => [data, ...prev]);
      setShowCreate(false);
      setForm({ name: '', product_id: '', page_id: '', flow_type: 'page_checkout_delivery' });
      toast.success('Funnel created');
    } catch { toast.error('Failed'); }
  };

  const deleteFunnel = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/funnels/${id}`);
    setFunnels(prev => prev.filter(f => f.id !== id));
    toast.success('Deleted');
  };

  return (
    <div className="space-y-6" data-testid="funnels-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Outfit']">Funnels</h1>
          <p className="text-muted-foreground mt-1">{funnels.length} funnels</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild><Button size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white"><Plus className="w-4 h-4 mr-1" />New Funnel</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Funnel</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Cash Flow Guide Funnel" /></div>
              <div className="space-y-1">
                <Label>Product</Label>
                <Select value={form.product_id} onValueChange={v => setForm({ ...form, product_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Landing Page (optional)</Label>
                <Select value={form.page_id} onValueChange={v => setForm({ ...form, page_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select page" /></SelectTrigger>
                  <SelectContent>{pages.map(p => <SelectItem key={p.id} value={p.id}>{p.title || p.slug}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Flow Type</Label>
                <Select value={form.flow_type} onValueChange={v => setForm({ ...form, flow_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="page_checkout_delivery">Page → Checkout → Delivery</SelectItem>
                    <SelectItem value="teaser_checkout_delivery">Teaser → Checkout → Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={create} className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white">Create Funnel</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {funnels.map(f => (
          <Card key={f.id} data-testid={`funnel-card-${f.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <GitBranch className="w-4 h-4 text-[#1A9DA5]" />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteFunnel(f.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
              <h3 className="font-semibold text-sm font-['Outfit']">{f.name}</h3>
              <Badge variant="outline" className="mt-2 text-xs">{f.flow_type?.replace(/_/g, ' ')}</Badge>
              <p className="text-xs text-muted-foreground mt-1">{products.find(p => p.id === f.product_id)?.title || 'Unknown product'}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
